import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from "react-hook-form";

import '../../../../assets/vendor/css/pages/page-auth.css';
import 'react-phone-input-2/lib/style.css'

import { getFormErrorMessage } from '../../../../config/global';
import { useProfileContext } from '../../../Contexts/ProfileContext';

import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import PhoneInput from 'react-phone-input-2'

import { useSelector } from 'react-redux';
import TimezonePicker from '../../../../components/ui/TimezonePicker';

// (Retired: a 12-entry curated list. It could not display Africa/Tripoli — a
// zone this very account uses — so the field rendered blank, and the `value ||
// 'Asia/Amman'` default below made an UNSET timezone look configured. Both are
// fixed by the shared full-IANA picker.)

function ProfileForm({ profileType, submitHandler, data, showImagePicker = true, showPhone = true }) {
    const { t } = useTranslation();

    const { previewImg, setPreviewImg, newImage, setNewImage } = useProfileContext();
    const companyDetails = useSelector((state) => state.global.companyDetails);

    const { formState: { errors }, reset, handleSubmit, control } = useForm();

    const previewImage = (e) => {
        setPreviewImg(prev => ({ ...prev, [profileType]: URL.createObjectURL(e.target.files[0]) }))
        setNewImage(prev => ({ ...prev, [profileType]: e.target.files[0] }));
    }

    const cancelHandler = (e) => {
        if (profileType == "company") {
            setPreviewImg(prev => ({ ...prev, [profileType]: companyDetails?.company?.logo ?? prev[profileType] }))
        } else {
            setPreviewImg(prev => ({ ...prev, [profileType]: localStorage.getItem("image") ?? prev[profileType] }))
        }
    }

    useEffect(() => {
        reset(data)
    }, [])

    return (
        <form onSubmit={handleSubmit(submitHandler)} className="mb-3" encType="multipart/form-data" >
            <div className="card mb-4">
                <h5 className="card-header text-capitalize">
                    {profileType === "company" ? t('profile.company_profile_details') : t('profile.user_profile_details')}
                </h5>
                {/* Account image picker — shown for user profile; company logo has its own section */}
                {showImagePicker && (
                    <>
                        <div className="card-body">
                            <div className="d-flex align-items-start align-items-sm-center gap-4">
                                <img src={previewImg[profileType]} alt={t('profile.avatar_alt')} accept="image/png, image/jpg, image/jpeg" className="d-block rounded" height={100} width={100} id="uploadedAvatar" />
                                <div className="button-wrapper">
                                    <label htmlFor={`${profileType}_upload`} className="btn btn-primary me-2 mb-4" tabIndex={0}>
                                        <span className="d-none d-sm-block">{t('profile.choose_new_photo')}</span>
                                        <i className="bx bx-upload d-block d-sm-none" />
                                        <Controller name="name" control={control}
                                            render={({ field, fieldState }) => (
                                                <input onChange={previewImage} type="file" id={`${profileType}_upload`} hidden accept="image/png, image/jpeg" />
                                            )} />
                                    </label>
                                    <button type="button" className="btn btn-outline-secondary account-image-reset mb-4"
                                        onClick={cancelHandler}>
                                        <i className="bx bx-reset d-block d-sm-none" />
                                        <span className="d-none d-sm-block" >{t('buttons.cancel')}</span>
                                    </button>
                                    <p className="text-muted mb-0">{t('profile.allowed_image_types')}</p>
                                </div>
                            </div>
                        </div>
                        <hr className="my-0" />
                    </>
                )}
                <div className="card-body">
                    <div className="d-flex flex-wrap">
                        {/* Name */}
                        <div className="mb-3 col-12 col-md-6">
                            <div className="field">
                                <label className="form-label">
                                    {profileType === "company" ? t('profile.company_name_label') : t('profile.user_name_label')}
                                </label>
                                <span className="p-float-label mb-1">
                                    <Controller name="name" control={control}
                                        rules={{ required: t('profile.name_required') }}
                                        render={({ field, fieldState }) => (
                                            <InputText id={field.name} {...field}
                                                className={`w-100 ${classNames({ 'p-invalid': fieldState.invalid })}`} />
                                        )} />
                                </span>
                                {getFormErrorMessage('name', errors)}
                            </div>
                        </div>

                        {/* phone */}
                        {showPhone && <div className="mb-3 col-12 col-md-6 form-password-toggle">
                            <div className="field ps-md-3">
                                <label className="form-label">{t('form.phone')}</label>
                                <span className="p-float-label mb-1">
                                    <Controller
                                        control={control}
                                        name="phone"
                                        rules={{
                                            required: t('profile.phone_required'),
                                            minLength: {
                                                value: 10,
                                                message: t('profile.phone_too_short'),
                                            },
                                            maxLength: {
                                                value: 13,
                                                message: t('profile.phone_too_long'),
                                            }
                                        }}
                                        render={({ field: { ref, ...field }, fieldState }) => (
                                            <PhoneInput
                                                {...field}
                                                inputExtraProps={{
                                                    ref,
                                                    required: true,
                                                    autoFocus: true
                                                }}
                                                countryCodeEditable={false}
                                                specialLabel={t('profile.mobile_number_label')}
                                                inputClass={`w-100 py-3 ${classNames({ 'p-invalid': fieldState.invalid })}`}
                                                containerClass={`w-100 ${classNames({ 'p-invalid': fieldState.invalid })}`}
                                            />
                                        )}
                                    />
                                </span>
                                {getFormErrorMessage('phone', errors)}
                            </div>
                        </div>}

                        {/* email */}
                        <div className="mb-3 col-6">
                            <div className="field">
                                <label className="form-label">{t('form.email')}</label>
                                <span className="p-float-label">
                                    <Controller name="email" control={control}
                                        rules={{
                                            required: t('profile.email_required'),
                                            pattern: {
                                                value: /^\w+([\.-]?\w+)*\+?\w*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                                                message: t('profile.email_invalid'),
                                            }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <InputText id={field.email} {...field} disabled={profileType == "user"}
                                                className={`w-100 ${classNames({ 'p-invalid': fieldState.invalid })}`} />
                                        )} />
                                </span>
                                {getFormErrorMessage('email', errors)}
                            </div>
                        </div>

                        {/* timezone (company only) — drives scheduling + auto campaigns' 9am-local send */}
                        {profileType === "company" && (
                            <div className="mb-3 col-6">
                                <div className="field">
                                    <label className="form-label">{t('profile.timezone_label')}</label>
                                    {/* value passed through as-is: a NULL timezone must read
                                        "Not set", never a plausible-looking default the owner
                                        assumes is configured. */}
                                    <Controller name="timezone" control={control}
                                        render={({ field }) => (
                                            <TimezonePicker
                                                value={field.value || null}
                                                onChange={field.onChange}
                                                allowUnset
                                            />
                                        )} />
                                    <small className="text-muted d-block mt-1">{t('profile.timezone_hint')}</small>
                                    <small className="text-muted d-block mt-1 fst-italic">
                                        {t('profile.timezone_precedence', 'A branch with its own timezone overrides this for the screens in that branch.')}
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='col-12 d-flex justify-content-end' >
                        <button className="btn btn-primary d-grid  mt-3">{t('profile.save_changes')}</button>
                    </div>
                </div>
                {/* /Account */}
            </div>
        </form>
    )
}

export default ProfileForm